import DOMPurify from "dompurify";

/**
 * Email HTML is attacker-controlled, even when it was successfully parsed by
 * the inbound MIME pipeline. Keep the enforcement at the render boundary so
 * both historical rows and newly-ingested mail are covered.
 *
 * DOMPurify's HTML profile preserves normal email markup and inline styles,
 * while removing executable elements, event-handler attributes, and unsafe
 * URL schemes.
 *
 * The profile also allows <style>/<link>. By default we strip them: HTML
 * rendered inline in the app document would let a sender's stylesheet restyle
 * the app chrome — a Google Calendar RSVP body was enough to centre every
 * subject line and flatten the toolbar tabs. `allowStyle` keeps <style> (hoisted
 * out of <head> so it survives) for bodies rendered inside <EmailFrame>, where
 * the stylesheet can only reach the frame's own document.
 */
export function sanitizeEmailHtml(html: string, { allowStyle = false } = {}): string {
    const root = DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        FORBID_TAGS: allowStyle ? ["link", "base"] : ["style", "link", "base"],
        WHOLE_DOCUMENT: allowStyle,
        RETURN_DOM: true,
    }) as HTMLElement;
    const body = root.querySelector("body") ?? root;
    if (allowStyle) body.prepend(...root.querySelectorAll("head style"));
    // Links open a new tab with no opener: inside the sandboxed frame an opener
    // would let the destination page navigate the frame (reverse tabnabbing).
    for (const a of body.querySelectorAll("a[href]")) {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
    }
    return body.innerHTML;
}
